/** @jsx CanvaSX */
import { CanvaSX, renderElement, withContext } from '@lib/canvasx/canvasx';
import ShapeBase from './ShapeBase';
import { Rect } from '@lib/canvasx/components/Rect';
import { Text } from '@lib/canvasx/components/Text';
import { FlexRow } from '@lib/canvasx/components/FlexRow';
import { FlexColumn } from '@lib/canvasx/components/FlexColumn';
import { Style } from '@lib/canvasx/components/Style';
import { canvasCssValue } from '@lib/canvasx/util';
import {
  RECORD_FONT_SIZE,
  RECORD_LINE_HEIGHT,
  RECORD_TITLE_FONT_SIZE,
} from './RecordDecorator';
import { type ToolList } from '../fabric/ObjectBehaviors';
import LinearResizeControl from '../controls/LinearResizeControl';
import { Image } from '@lib/canvasx/components/Image';
import { hasIntegrationRecordReference } from '../objects/guards';
import tools from '../tools';

const RECORD_MINIMUM_WIDTH = 200;

// returns handler to open the record in a new tab, or undefined if not possible
export const openIntegrationRecordHandler = (obj: fabric.Object) => {
  if (hasIntegrationRecordReference(obj)) {
    const { integrationRecord } = obj;

    // TODO handle missing recordData - do we just construct the URL?
    const url = integrationRecord.recordData?.url;

    if (url) {
      return () => window.open(url, '_blank', 'noopener,noreferrer');
    }
  }
};

class ShapeIntegrationRecord extends ShapeBase {
  _sanitizedText?: string;

  constructor(fabricObject: ShapeBase['fabricObject']) {
    super(fabricObject);

    this.fabricObject.uniformScaling = true;
    this.fabricObject.strokeWidth = 0;
    this.fabricObject.opacity = 1;
    this.fabricObject.stroke = undefined;

    if (this.fabricObject.width < RECORD_MINIMUM_WIDTH) {
      this.fabricObject.set('width', ShapeIntegrationRecord.defaultWidth());
    }
    if (this.fabricObject.height !== ShapeIntegrationRecord.defaultHeight()) {
      this.fabricObject.set('height', ShapeIntegrationRecord.defaultHeight());
    }

    // Force the record to have consistent scale.
    if (this.scaleX !== this.scaleY) {
      this.fabricObject.set('scaleX', this.fabricObject.scaleY);
    }
  }

  static iconClass() {
    return 'fa-regular fa-credit-card-front';
  }

  static defaultWidth() {
    return RECORD_MINIMUM_WIDTH;
  }

  static defaultHeight() {
    return 68;
  }

  static shapeControls = {
    ...this.createDefaultControls(),
    mr: new LinearResizeControl({ edge: 'right' }),
    ml: new LinearResizeControl({ edge: 'left' }),
  };

  minDimension() {
    return RECORD_MINIMUM_WIDTH;
  }

  maxDimension() {
    return Number.MAX_SAFE_INTEGER;
  }

  hasEmbeddedText() {
    return false;
  }

  get description(): string {
    return this.fabricObject.integrationRecord?.recordData?.name ?? '';
  }

  allowedStyleCopyPasteFields(): string[] {
    return [];
  }

  tools() {
    return [[tools.ToolShapeType], [tools.ToolOpenRecord]];
  }

  textTools(): ToolList {
    return [];
  }

  get shouldRenderText(): boolean {
    return false;
  }

  render(ctx: CanvasRenderingContext2D): void {
    const { integrationRecord } = this.fabricObject;

    if (!integrationRecord) {
      console.error('No integration record data');
      return;
    }

    const { width, height } = this;

    const x = -width / 2;
    const y = -height / 2;
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(1 / this.scaleX, 1 / this.scaleY);

    withContext(
      {
        canvas: this.canvas,
        ctx,
        object: this.fabricObject,
        // This assumes records have the same X/Y scale
        scale: this.scaleX,
      },
      () => {
        const { key, status, assignee, type, priority, name } =
          integrationRecord.recordData ?? {};

        const openRecordHandler = openIntegrationRecordHandler(
          this.fabricObject
        );

        const borderRadius = 4;

        // Disable the hover if we have an active transform
        const disableHover = !!this.fabricObject.canvas._currentTransform;
        const hoveringName =
          this.fabricObject.hoveredKey === 'name' && !disableHover;

        const recordColor = status?.color || '#ccc';

        renderElement(
          <Style fill={canvasCssValue('--theme-tertiary-text', '#999999')}>
            <Rect
              borderRadius={borderRadius}
              border={recordColor}
              fill={canvasCssValue('--theme-primary-background', '#ffffff')}
              width={width}
              height={height}
            />

            <Rect
              width={borderRadius * 2}
              height={height}
              borderRadius={borderRadius}
              fill={recordColor}
              clip={{ x: 0, y: 0, width: borderRadius, height }}
            />

            <FlexColumn
              x={borderRadius}
              width={width - borderRadius}
              padding={{ horizontal: 9, vertical: 5 }}
              horizontalAlignment='stretch'
            >
              <FlexRow verticalAlignment='center' gap={5}>
                {type?.icon && (
                  <Image
                    key='type'
                    src={type.icon}
                    width={12}
                    height={12}
                    tooltip={type.name}
                  />
                )}
                <Text size={RECORD_FONT_SIZE} lineHeight={RECORD_LINE_HEIGHT}>
                  {key ?? integrationRecord.recordKey}
                </Text>
                {priority?.icon && (
                  <Image
                    key='priority'
                    src={priority.icon}
                    width={12}
                    height={12}
                    tooltip={priority.name}
                  />
                )}

                <Rect flexGrow />

                {assignee &&
                  (assignee.avatar ? (
                    <Image
                      key='assignee'
                      src={assignee.avatar}
                      width={RECORD_FONT_SIZE}
                      height={RECORD_FONT_SIZE}
                      rounded
                      tooltip={assignee.name}
                    />
                  ) : (
                    <Text key='assignee' size={9} tooltip={assignee.name}>
                      {assignee.name.split(/\s+/).slice(0, 2).join('')}
                    </Text>
                  ))}

                {status && (
                  <Rect borderRadius={4} padding={3} fill={recordColor}>
                    <Text color='white'>{status.name}</Text>
                  </Rect>
                )}
              </FlexRow>

              <FlexRow
                gap={{ horizontal: 4, vertical: 0 }}
                maxRows={2}
                verticalAlignment='center'
              >
                <Text
                  key='name'
                  color={
                    hoveringName && openRecordHandler
                      ? canvasCssValue('--theme-link-text', '#0073cf')
                      : canvasCssValue('--theme-primary-text', '#333333')
                  }
                  lineHeight={1.4}
                  size={RECORD_TITLE_FONT_SIZE}
                  onClick={openRecordHandler}
                >
                  {name ?? 'Loading...'}
                </Text>
              </FlexRow>
            </FlexColumn>
          </Style>
        );
      }
    );

    ctx.restore();
  }
}

export default ShapeIntegrationRecord;
